
import { Helmet } from 'react-helmet-async';

interface PageHeadProps {
  title: string;
  description?: string;
  keywords?: string;
  ogImage?: string;
  noIndex?: boolean;
  canonicalUrl?: string;
  preload?: { href: string; as: string }[];
  backgroundImage?: string;
}

export const PageHead = ({ 
  title = "Fledgio Launch Offer - Free Rust Training",
  description = "Apply for our exclusive launch offer - free Rust training for qualified candidates. Limited spots available.",
  keywords = "Rust training, Rust programming, London Rust course, free Rust course",
  ogImage = "/lovable-uploads/f378e853-61f6-4d4e-8ceb-67eba5fcc775.png",
  noIndex = false,
  canonicalUrl = "https://www.fledgio.com/campaigns/rust-course-launch",
  preload = [],
  backgroundImage
}: PageHeadProps) => {
  // Ensure ogImage is an absolute URL
  const ogImageUrl = ogImage?.startsWith('http') 
    ? ogImage 
    : `${window.location.origin}${ogImage}`;
    
  return (
    <Helmet>
      {/* Basic Metadata */}
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      
      {/* Favicon */}
      <link rel="icon" href="/favicon.ico" />
      
      {/* Social Media / Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImageUrl} />
      
      {/* Canonical URL */}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      
      {/* Robots directive - for SEO control */}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}
      
      {/* Resource Preloading */}
      {/* Background Image Preload */}
      <link 
        rel="preload" 
        as="image" 
        href={ogImageUrl} 
        fetchPriority="high" 
      />
      
      {/* Custom preloads */}
      {preload.map((item, index) => (
        <link 
          key={`preload-${index}`}
          rel="preload"
          as={item.as}
          href={item.href}
        />
      ))}
      
      {/* Preload background image if provided */}
      {backgroundImage && (
        <link
          rel="preload"
          as="image"
          href={backgroundImage}
          fetchPriority="high"
        />
      )}
    </Helmet>
  );
};
